import { getConfig } from '@/config';
import type { ApiEndpoints } from '@/types';

// Use Vite proxy in development to avoid CORS issues
const isLocalDev = import.meta.env.DEV;

const getApiEnvironmentPath = (): string => {
  const { env, developmentPathOverride } = getConfig();
  const sanitizedOverride = developmentPathOverride?.trim().replace(/^\/+|\/+$/g, '');

  if (env === 'dev' && sanitizedOverride) {
    return sanitizedOverride;
  }

  return env;
};

export const getApiEndpoints = (): ApiEndpoints => {
  const authBaseUrl = getConfig().authBaseUrl;
  const authEnv = getConfig().authEnvironment;
  const authPath = getConfig().authBusinessPath;
  const authEnvPath = `${authBaseUrl}/${authEnv}`;
  const fullAuthPath = `${authBaseUrl}/${authEnv}/${authPath}`;

  // In local dev, use proxy; in production, use direct URL
  const coreBaseUrl = isLocalDev ? '/api-proxy' : getConfig().apiBaseUrl;
  const coreEnv = getApiEnvironmentPath();
  const corePath = getConfig().apiBusinessPath;
  const fullApiPath = `${coreBaseUrl}/${coreEnv}/${corePath}`;

  return {
    auth: {
      signup: `${fullAuthPath}/signup`,
      confirmSignup: `${fullAuthPath}/confirm-signup`,
      signin: `${fullAuthPath}/signin`,
      refresh: `${fullAuthPath}/refresh`,
      resendVerification: `${fullAuthPath}/resend-verification`,
      forgotPassword: `${fullAuthPath}/forgot-password`,
      confirmForgotPassword: `${fullAuthPath}/confirm-forgot-password`,
    },
    authAdmin: {
      listBusinessUsers: `${authEnvPath}/business/users-business`,
      listHomeownerUsers: `${authEnvPath}/business/users-homeowner`,
      updateBusinessUser: `${authEnvPath}/business/users-business`,
      updateBusinessUserEnabled: `${authEnvPath}/business/users-business/enabled`,
      confirmBusinessUser: `${authEnvPath}/business/users-business/confirm`,
    },
    core: {
      user: {
        getUser: `${fullApiPath}/user`,
        updateUser: `${fullApiPath}/user`,
        createUser: `${fullApiPath}/user`,
        adminBusinessProfile: `${fullApiPath}/admin/profiles/business`,
      },
      properties: {
        getProperties: `${fullApiPath}/properties`,
        getPropertiesByOrgID: `${fullApiPath}/properties`,
        createProperty: `${fullApiPath}/properties`,
        getPropertyById: `${fullApiPath}/properties`,
        updateProperty: `${fullApiPath}/properties`,
        deleteProperty: `${fullApiPath}/properties`,
        getPropertyEvents: `${fullApiPath}/properties`,
      },
      roles: {
        getRole: `${fullApiPath}/roles`,
        getRolesByOrg: `${fullApiPath}/roles`,
        createRole: `${fullApiPath}/roles`,
        updateRole: `${fullApiPath}/roles`,
        deleteRole: `${fullApiPath}/roles`,
      },
      organizations: {
        getOrganization: `${fullApiPath}/organizations`,
        updateOrganization: `${fullApiPath}/organizations`,
        createOrganization: `${fullApiPath}/organizations`,
        checkSlug: `${fullApiPath}/organizations/check-slug`,
      },
      organizationMembers: {
        getOrganizationMembers: `${fullApiPath}/organization-members`,
        addOrganizationMember: `${fullApiPath}/organization-members`,
        removeOrganizationMember: `${fullApiPath}/organization-members`,
        updateOrganizationMember: `${fullApiPath}/organization-members`,
      },
      invitations: {
        getInvitations: `${fullApiPath}/invitations`,
        getInvitation: `${fullApiPath}/invitations`,
        createInvitation: `${fullApiPath}/invitations`,
        deleteInvitation: `${fullApiPath}/invitations`,
        hardDeleteInvitation: `${fullApiPath}/invitations/delete`,
        resendInvitation: `${fullApiPath}/invitations/resend`,
        verifyInvitation: `${fullApiPath}/invitations/verify`,
        acceptInvitation: `${fullApiPath}/invitations/accept`,
      },
      members: {
        getMembers: `${fullApiPath}/team`,
        updateMember: `${fullApiPath}/team`,
        removeMember: `${fullApiPath}/team`,
      },
      homeowners: {
        getHomeowner: `${fullApiPath}/homeowners`,
        listHomeowners: `${fullApiPath}/admin/homeowners`,
      },
      adminMobileConfig: {
        getMobileConfig: `${fullApiPath}/admin/settings`,
        updateMobileConfig: `${fullApiPath}/admin/settings`,
      },
      propertySchemas: {
        listSchemas: `${fullApiPath}/schemas`,
        getSchema: `${fullApiPath}/schemas`,
        createSchema: `${fullApiPath}/schemas`,
        updateSchema: `${fullApiPath}/schemas`,
        deprecateSchema: `${fullApiPath}/schemas/deprecate`,
        publishSchema: `${fullApiPath}/schemas/publish`,
        deleteSchema: `${fullApiPath}/schemas`,
        cloneSchema: `${fullApiPath}/schemas/clone`,
      },
      propertyTemplates: {
        listTemplates: `${fullApiPath}/property-templates`,
        getTemplate: `${fullApiPath}/property-templates`, // use ?id=
        createTemplate: `${fullApiPath}/property-templates`, // PUT (upsert)
        updateTemplate: `${fullApiPath}/property-templates`, // PUT + ?id=
        publishTemplate: `${fullApiPath}/property-templates/publish`, // POST + ?id=
        deprecateTemplate: `${fullApiPath}/property-templates/deprecate`, // POST + ?id=
        deleteTemplate: `${fullApiPath}/property-templates`, // DELETE + ?id=
        cloneTemplate: `${fullApiPath}/property-templates/clone`, // POST + ?id= (when implemented)
      },
      adminSubscriptions: {
        getSubscriptions: `${fullApiPath}/admin/subscriptions`,
        updateSubscription: `${fullApiPath}/admin/subscriptions`,
        assignSubscription: `${fullApiPath}/admin/subscriptions/assign`,
      },
      pushNotifications: {
        getDevices: `${fullApiPath}/push-notifications/devices`,
        send: `${fullApiPath}/push-notifications/send`,
      },
      support: {
        getTickets: `${fullApiPath}/support`,
        replyToTicket: `${fullApiPath}/support`,
        updateTicketStatus: `${fullApiPath}/support`,
      },
      recoveryCases: {
        listCases: `${fullApiPath}/admin/recovery-cases`,
        getCase: `${fullApiPath}/admin/recovery-cases`,
        approveCase: `${fullApiPath}/admin/recovery-cases`,
        rejectCase: `${fullApiPath}/admin/recovery-cases`,
        revokeApproval: `${fullApiPath}/admin/recovery-cases`,
        cancelCase: `${fullApiPath}/admin/recovery-cases`,
        executeCase: `${fullApiPath}/admin/recovery-cases`,
      },
      locations: {
        getLocations: `${fullApiPath}/locations`,
        createLocation: `${fullApiPath}/locations`,
        updateLocation: `${fullApiPath}/locations`,
        deleteLocation: `${fullApiPath}/locations`,
      },
      propertySystems: {
        getPropertySystems: `${fullApiPath}/property-systems`,
        createPropertySystem: `${fullApiPath}/property-systems`,
        updatePropertySystem: `${fullApiPath}/property-systems`,
        deletePropertySystem: `${fullApiPath}/property-systems`,
      },
      propertyUtilities: {
        getUtilities: `${fullApiPath}/utilities`,
        createUtility: `${fullApiPath}/utilities`,
        updateUtility: `${fullApiPath}/utilities`,
        deleteUtility: `${fullApiPath}/utilities`,
      },
      propertyHandover: {
        initiate: `${fullApiPath}/property-handover`,
        claim: `${fullApiPath}/property-handover`,
        cancel: `${fullApiPath}/property-handover`,
        active: `${fullApiPath}/property-handover`,
      },
      adminCorePaths: {
        batchCreateProperty: `${fullApiPath}/admin/db-ops/run`,
      },
      phoneVerification: {
        sendVerification: `${fullApiPath}/phone-verification/start`,
        verifyCode: `${fullApiPath}/phone-verification/confirm`,
      },
      storage: {
        fetchSignedMediaUrls: `${fullApiPath}/storage`,
        uploadMedia: `${fullApiPath}/storage`,
        deleteMedia: `${fullApiPath}/storage`,
        deleteMediaBatch: `${fullApiPath}/storage`,
      },
      propertyDocumentsVault: {
        base: `${fullApiPath}/properties`,
      },
      propertyMembers: {
        base: `${fullApiPath}/properties`,
        profilesBase: `${fullApiPath}/profiles`,
      },
      propertyStatistics: {
        base: `${fullApiPath}/properties`,
      },
    },
  };
};
